import { Agent, CoverUrl, SearchResult } from 'search/models/search-results';
import { Concept, EntityTypes, TabEntity } from '../../../entity/models/entity';
import { SyndeticsWidget } from '../../../services/syndetics-unbound.service';

export enum ShowcasesTypes {
  RESOURCES = 'resources',
  CUSTOM = 'staffLists',
  SHELF = 'shelf',
  NYT = 'nyTimes',
  CONTRIBUTORS = 'contributors',
  GENRE = 'genre',
  TOPICS = 'topics',
}

export type FrcShowcaseType = ShowcasesTypes | SyndeticsWidget;

export enum FrcShowcaseSource {
  vegaContextEngine = 'vegaContextEngine',
  syndeticsUnbound = 'syndeticsUnbound',
  libraryCreatedContent = 'libraryCreatedContent',
  nytApi = 'nytApi',
}

export interface ShowcaseDisplay {
  type: FrcShowcaseType;
  source: FrcShowcaseSource;
}

export interface FrcShowcase extends ShowcaseDisplay {
  showcase?: ExtractedShowcase;
}

export interface ExtractedShowcase {
  type: ShowcasesTypes;
  data: (ExtractedSingleShowcaseResource | ExtractedMultipleShowcase | any)[];
  identifier: string;
  showcaseId: string;
  name: string;
  totalCount: number;
  loading: boolean;
  metadata: CustomShowcaseMetadataInfo;
  isMultiple?: boolean;
}

export interface ExtractedSingleShowcaseResource {
  id: string;
  entityType: EntityTypes;
  title: string;
  author: TabEntity;
  date: string;
  isUnresolved?: boolean,
  coverConfig: {
    coverUrl: CoverUrl;
    type: EntityTypes;
  },
}

export interface ExtractedMultipleShowcaseResource {
  id: string;
  entityType: EntityTypes;
  isUnresolved?: boolean,
  title: string;
  coverConfig: {
    coverUrl: CoverUrl;
    type: EntityTypes;
  },
}

export interface ExtractedMultipleShowcase {
  id: string;
  label: string;
  identifier: string;
  description: string;
  totalResources: number;
  isExtendable: boolean;
  resources: ExtractedMultipleShowcaseResource[];
  metadata?: CustomShowcaseMetadataInfo;
}

export enum defaultTopicParams {
  maxTopicResourcesNumber = 8,
  maxConceptsNumber = 4,
}

export interface Contributor {
  id: string;
  label: string;
  relationshipTypes?: string[];
  resourceLinksCount?: number;
  coverUrl?: CoverUrl;
}

export enum CarouselSwitchTypes {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum SHOWCASES_PARAMS {
  RESOURCES_PER_SHOWCASE_PAGE = 12,
  RESOURCES_PER_SHOWCASE_RESOURCE_PAGE = 6,
  RESOURCES_PER_NYTSHOWCASE_RESOURCE_PAGE = 5,
  RESOURCES_PER_NYTSHOWCASE_RESOURCE_PAGE_MOBILE = 3,
  CONTRIBUTORS_CAROUSEL = 13,
  TOPICS_PER_SHOWCASE_PAGE = 4,
  TOPICS_PER_SHOWCASE_RESOURCE_PAGE = 2,
  TOPICS_PER_FULL_PAGE = 20,
  TOPICS_PER_LOAD_MORE = 10,
  SINGLE_SHOWCASE_LOAD_MORE = 48,
  SINGLE_SHOWCASE_SEE_ALL = 96,
}

export interface ShowcasesEntity {
  id: string;
  entityType: EntityTypes;
  title: string;
  isbn?: string;
  agentId?: string;
}

export interface ShowcasesRequestParams {
  id: string;
  entityType: EntityTypes;
  targetType: EntityTypes.INSTANCE | EntityTypes.FORMAT_GROUP;
}

export type ShowcasesResponse = {
  [key in ShowcasesTypes]: ShowcaseData;
};

export interface CustomShowcaseMetadataInfo {
  id: string;
  name: string;
  creator: string;
}

export interface ShowcaseData {
  hits: ShowcaseHit[];
  name: string;
  showcaseId?: string;
  totalResults: number;
}
export interface ShowcaseHit {
  identifier: string;
  relatedResources: HitRelatedResource;
  resource?: Concept;
  metadata?: CustomShowcaseMetadataInfo;
}

export interface ShowcaseData {
  hits: ShowcaseHit[];
  name: string;
  showcaseId?: string;
  page: number;
  totalPages: number;
  totalResults: number;
}

export interface CustomShowcaseFormatGroupDto {
  id: string;
  entityType: EntityTypes;
  title: string;
  publicationDate: string;
  coverUrl: CoverUrl;
  primaryAgent: TabEntity;
}

export interface CustomShowcaseAgentOrConceptDto {
  id: string;
  entityType: EntityTypes;
  type: string;
  label: string;
  coverUrl?: CoverUrl;
}

export type CustomShowcaseEntityDto = CustomShowcaseFormatGroupDto | CustomShowcaseAgentOrConceptDto;

export interface StoredShowcaseData {
  data: any[];
  type: ShowcasesTypes;
  identifier: string;
  showcaseId: string;
  totalCount: number;
  name: string;
  loading: boolean;
  isMultiple?: boolean;
  metadata: CustomShowcaseMetadataInfo;
}

export interface HitRelatedResource {
  totalResults: number;
  totalPages?: number;
  page?: number;
  data: Concept[] | Agent[] | SearchResult[];
}

export interface MultipleShowcaseRequestParams extends ShowcasesRequestParams {
  pageNum?: number;
  pageSize?: number;
  showcaseId?: string;
}

export interface SingleShowcaseRequestParams extends ShowcasesRequestParams {
  showcase: ShowcasesTypes | string;
  identifier: string;
  showcaseId?: string;
  pageNum?: number;
  pageSize?: number;
}

export interface ShowcasePayload {
  id: string;
  entityType: EntityTypes;
  targetType?: EntityTypes.INSTANCE | EntityTypes.FORMAT_GROUP;
}

export interface ShowcaseLoadMorePayload extends ShowcasePayload {
  showcase: ShowcasesTypes;
  identifier: string;
  showcaseId?: string;
  pageSize?: number;
  isMouseClick?: boolean;
}

export enum NYTShowcasesList {
  Fiction = 'Combined Print & E-Book Fiction',
  Nonfiction = 'Combined Print & E-Book Nonfiction',
  YoungAdult = 'Young Adult',
  MiddleGrade = 'Children’s Middle Grade',
  PictureBooks = 'Children’s Picture Books',
}

export const defaultShowcaseDisplayOrder: ShowcaseDisplay[] = [
  {
    type: SyndeticsWidget.series,
    source: FrcShowcaseSource.syndeticsUnbound,
  },
  {
    type: ShowcasesTypes.CUSTOM,
    source: FrcShowcaseSource.libraryCreatedContent,
  },
  {
    type: ShowcasesTypes.NYT,
    source: FrcShowcaseSource.nytApi,
  },
  {
    type: SyndeticsWidget.readinglevel,
    source: FrcShowcaseSource.syndeticsUnbound,
  },
  {
    type: SyndeticsWidget.similar,
    source: FrcShowcaseSource.syndeticsUnbound,
  },
  {
    type: ShowcasesTypes.RESOURCES,
    source: FrcShowcaseSource.vegaContextEngine,
  },
  {
    type: ShowcasesTypes.TOPICS,
    source: FrcShowcaseSource.vegaContextEngine,
  },
  {
    type: SyndeticsWidget.patronreviews,
    source: FrcShowcaseSource.syndeticsUnbound,
  },
  {
    type: SyndeticsWidget.reviews,
    source: FrcShowcaseSource.syndeticsUnbound,
  },
  {
    type: SyndeticsWidget.awards,
    source: FrcShowcaseSource.syndeticsUnbound,
  },
  {
    type: SyndeticsWidget.bookprofile,
    source: FrcShowcaseSource.syndeticsUnbound,
  },
  {
    type: SyndeticsWidget.author,
    source: FrcShowcaseSource.syndeticsUnbound,
  },
  {
    type: ShowcasesTypes.CONTRIBUTORS,
    source: FrcShowcaseSource.vegaContextEngine,
  },
];
